import _ from 'lodash';
import React, { useMemo } from 'react';
import { Avatar, Button, Divider, Tag, Text } from 'src/@vymo/ui/atoms';
import { Card, NoData, Popup } from 'src/@vymo/ui/blocks';
import SkeletonLoader from 'src/@vymo/ui/blocks/skelton';
import { ReactComponent as Email } from 'src/assets/icons/emailAt.svg';
import { ReactComponent as LocationIcon } from 'src/assets/icons/googleLocation.svg';
import { ReactComponent as GmeetIcon } from 'src/assets/icons/googleMeet.svg';
import { ReactComponent as Phone } from 'src/assets/icons/phone.svg';
import { Keys, locale } from 'src/i18n';
import { useAppSelector } from 'src/store/hooks';
import { getClientConfigData } from 'src/workspace/utils';
import { getTruncatedString } from '../queries';
import { getCardData, getError, getIsLoading } from '../selectors';
import styles from '../index.module.scss';

function Profile() {
  const config = getClientConfigData();
  const {
    user_header_information: userHeaderInfo,
    branding,
  }: { user_header_information: any; branding: any } = config;
  const { name, lobs, ...headerFields } = userHeaderInfo;

  const userData = useAppSelector((state) => getCardData(state, 'header'));
  const isLoading = useAppSelector((state) => getIsLoading(state, 'header'));
  const error = useAppSelector((state) => getError(state, 'header'));

  const userLabels: JSX.Element[] = useMemo(() => {
    const inputsMap = userData?.inputs_map || {};
    const allLabels: JSX.Element[] = [];

    const tags = Object.entries(headerFields)
      .map(([key]) => _.get(inputsMap, key, ''))
      .filter(Boolean);

    const attributes =
      lobs
        ?.flatMap((lob) => [
          _.get(inputsMap, lob?.lob_type, ''),
          _.get(inputsMap, lob?.lob_name, ''),
        ])
        ?.filter(Boolean) || [];

    const createTag = (value: string, variant: 'info' | 'warning') => (
      <Tag
        key={`${variant}-${value}`}
        variant={variant}
        className={styles.profileCard__header__tags__tag}
      >
        <Text type="label" bold>
          {getTruncatedString(value)}
        </Text>
      </Tag>
    );

    attributes.forEach((attr) => allLabels.push(createTag(attr, 'warning')));
    tags.forEach((tag) => allLabels.push(createTag(tag, 'info')));

    return allLabels;
  }, [headerFields, lobs, userData]);

  const getChildren = () => {
    if (isLoading) {
      return <SkeletonLoader avtar lines={4} width={280} height={200} />;
    }

    if (error) {
      return <NoData message={locale(Keys.ERROR_FETCHING_PROFILE_DATA)} />;
    }

    if (!userData || _.isEmpty(userData)) {
      return <NoData message={locale(Keys.NO_PROFILE_DATA_FOUND)} />;
    }

    return (
      <div className={styles.profileCard}>
        <div className={styles.profileCard__header}>
          <Avatar
            imageUrl={userData.profile_photo_s3_link}
            text={(userData?.name || name)?.charAt(0)}
            shape="circle"
            background={branding?.colors?.secondary}
            size="medium"
          />
          <div className={styles.profileCard__header__info}>
            <Text type="h4">{userData.name || name}</Text>
            <div className={styles.profileCard__header__tags}>
              {userLabels.slice(0, 2).map((userLabel) => userLabel)}
              {userLabels.length > 2 && (
                <Popup
                  content={
                    <div
                      className={
                        styles.profileCard__header__tags__placeholder__items
                      }
                    >
                      {userLabels.slice(2).map((userLabel) => userLabel)}
                    </div>
                  }
                  openTrigger="click"
                  closeTrigger="click"
                  placement="right"
                >
                  <Tag
                    variant="info"
                    className={styles.profileCard__header__tags__tag}
                  >
                    <Text type="label" bold>
                      {`+${userLabels.length - 2}`}
                    </Text>
                  </Tag>
                </Popup>
              )}
            </div>
          </div>
        </div>
        <Divider />
        <div className={styles.profileCard__attributesWrapper}>
          <div className={styles.profileCard__attributesWrapper__attribute}>
            <Text
              classNames={styles.profileCard__attributesWrapper__attribute__key}
            >
              <Phone />
              Phone :
            </Text>
            <Text>{userData?.phone || '-'}</Text>
          </div>
          <div className={styles.profileCard__attributesWrapper__attribute}>
            <Text
              classNames={styles.profileCard__attributesWrapper__attribute__key}
            >
              <Email />
              Email :
            </Text>
            <Text>{userData?.email || '-'}</Text>
          </div>
        </div>
        <Divider />
        <div className={styles.profileCard__actionsWrapper}>
          <Button
            className={styles.profileCard__actionsWrapper__action}
            disabled={_.isEmpty(userData?.last_location)}
            iconProps={{
              iconPosition: 'left',
              icon: <LocationIcon />,
            }}
            onClick={() =>
              window.open(
                `https://www.google.com/maps/search/?api=1&query=${userData?.last_location?.latitude},${userData?.last_location?.longitude}`,
              )
            }
          >
            Address
          </Button>
          <Button
            className={styles.profileCard__actionsWrapper__action}
            disabled={_.isEmpty(userData?.inputs_map?.google_meet_link)}
            iconProps={{
              iconPosition: 'left',
              icon: <GmeetIcon />,
            }}
            onClick={() =>
              window.open(
                userData?.inputs_map?.google_meet_link || '',
                '__blank',
              )
            }
          >
            Meet
          </Button>
        </div>
      </div>
    );
  };

  return <Card classNames={styles.profileCardWrapper}>{getChildren()}</Card>;
}

export default Profile;
